import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { formatarMoedaBrasileira } from "../../../utils/formatarMoedaBrasileira";
import { useAuth } from "../../../context/authContext";
import ComissaoService from "../../../services/resource/ComissaoService";
import { ANOS, MESES } from "../../../utils/options";
import { formatLocalDateTime } from "../../../utils/formatDate";
import { Dialog } from "primereact/dialog";
import ParametrosSnapshotView from "../ParametrosSnapshotView";
import { useNavigate } from "react-router-dom";

export default function MinhaComissao() {
    const { user } = useAuth();
    const perfil = user.perfis || [];
    const comissaoService = useMemo(() => new ComissaoService(), []);
    const navigate = useNavigate()

    const now = new Date();
    let anoInicial = now.getFullYear();
    let mesInicial = now.getMonth() + 1;
    if (now.getDate() >= 27) {
        mesInicial = mesInicial === 12 ? 1 : mesInicial + 1;
        if (mesInicial === 1) anoInicial += 1;
    }

    const [ano, setAno] = useState(anoInicial);
    const [mes, setMes] = useState(mesInicial);
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalParametros, setModalParametros] = useState(false);

    useEffect(() => {
        setLoading(true);
        setDados(null);
        comissaoService
            .minhaComissao({ ano, mes, funcionarioId: user.id })
            .then((res) => {
                setDados(res.data);
            })
            .catch(() => setDados(null))
            .finally(() => setLoading(false));
    }, [ano, mes]);

    const d = dados;
    const isLogistico = d?.tipoDistribuidor === "LOGISTICO";

    const navegarRotas = () => {
        if (isLogistico) {
            navigate(`/minha-comissao/cargas?ano=${ano}&mes=${mes}&funcionarioId=${d.funcionarioId}&filialId=${user.filialId}`);
        } else {
            navigate(`/minha-comissao/romaneios?ano=${ano}&mes=${mes}&funcionarioId=${d.funcionarioId}&filialId=${user.filialId}`);
        }
    };

    const navegarTelemetria = () => {
        if (isLogistico) {
            navigate(`/minha-comissao/telemetria-greenmile?ano=${ano}&mes=${mes}&funcionarioId=${d.funcionarioId}&filialId=${user.filialId}`);
        } else {
            navigate(`/minha-comissao/telemetria-concept?ano=${ano}&mes=${mes}&funcionarioId=${d.funcionarioId}&filialId=${user.filialId}`);
        }
    };

    const navegarAfastamentos = () => {
        navigate(`/minha-comissao/afastamentos?ano=${ano}&mes=${mes}&funcionarioId=${d.funcionarioId}&filialId=${user.filialId}&tipoDistribuidor=${d.tipoDistribuidor}&funcionarioCodigo=${d.funcionarioCodigo}`);
    };

    return (
        <div className="min-h-screen bg-background">

            {/* ── Header ── */}
            <div className="bg-card border-b border-border">
                <div className="px-4 pt-4 pb-0">
                    <div className="flex items-start justify-between gap-2 mb-3">
                        <div>
                            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Minha Comissão</p>
                            <h1 className="text-base font-bold font-display text-foreground leading-tight">
                                {user.nome ?? "—"}
                            </h1>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {d?.filialNome ?? user.filialNome ?? "—"} · {d?.funcionarioCargo ?? "—"}
                            </p>
                        </div>
                        {d && (
                            <div className="flex-shrink-0 text-right">
                                <p className="text-xs text-muted-foreground">A receber</p>
                                <p className="text-xl font-extrabold text-teal-600 leading-tight">
                                    {brl(d.valorTotalReceber)}
                                </p>
                                {d.fechado
                                    ? <span className="text-[10px] text-teal-700 bg-teal-100 rounded-full px-2 py-0.5 font-medium">Fechado</span>
                                    : <span className="text-[10px] text-amber-700 bg-amber-100 rounded-full px-2 py-0.5 font-medium">Em aberto</span>
                                }
                            </div>
                        )}
                    </div>

                    {/* Period picker */}
                    <div className="flex items-center gap-2 pb-3 text-sm overflow-x-auto">
                        <i className="fa fa-calendar text-muted-foreground flex-shrink-0" />
                        <select
                            value={mes}
                            onChange={(e) => setMes(e.target.value)}
                            className="border-0 bg-transparent font-medium text-foreground focus:outline-none cursor-pointer text-sm"
                        >
                            {MESES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                        <span className="text-muted-foreground">|</span>
                        <select
                            value={ano}
                            onChange={(e) => setAno(Number(e.target.value))}
                            className="border-0 bg-transparent font-medium text-foreground focus:outline-none cursor-pointer text-sm"
                        >
                            {ANOS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                        {d?.dataAtualizacao && (
                            <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
                                Atualizado {formatLocalDateTime(d.dataAtualizacao)}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Content ── */}
            <div className="px-4 py-4 space-y-3 max-w-lg mx-auto">
                {loading || !d
                    ? <EmptyState loading={loading} />
                    : (
                        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">

                            {/* ── Resumo financeiro (sempre visível) ── */}
                            <div className="rounded-2xl bg-gradient-to-br from-teal-600 to-teal-700 p-4 text-white shadow-card">
                                <p className="text-xs font-medium opacity-70 mb-3 uppercase tracking-wide">Resumo Financeiro</p>
                                <div className="grid grid-cols-3 gap-2 text-center">
                                    <div>
                                        <p className="text-[10px] opacity-70">Sem desconto</p>
                                        <p className="text-sm font-bold">{brl(d.totalSemDesconto)}</p>
                                    </div>
                                    <div>
                                        <p className="text-[10px] opacity-70">Descontos</p>
                                        <p className="text-sm font-bold">{brl(d.descontos)}</p>
                                    </div>
                                    <div className="bg-white/15 rounded-xl py-1">
                                        <p className="text-[10px] opacity-80">A receber</p>
                                        <p className="text-base font-extrabold">{brl(d.valorTotalReceber)}</p>
                                    </div>
                                </div>
                                {d.porcentagemTrabalhado != null && (
                                    <div className="mt-3 pt-3 border-t border-white/20">
                                        <div className="flex justify-between text-xs opacity-80">
                                            <span>Proporcionalidade aplicada</span>
                                            <span className="font-semibold">{fmt(d.porcentagemTrabalhado != null ? d.porcentagemTrabalhado * 100 : null)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ── Visão geral dos indicadores ── */}
                            {perfil.includes("AUXILIAR_ENTREGA") ?
                                <div className="grid grid-cols-3 gap-2">
                                    <div className="rounded-xl border border-border bg-card p-3 flex flex-col items-center gap-1">
                                        <CircularProgress value={Number(d.percentualConclusao)} label="Conclusão de rota" />
                                    </div>
                                    <div className="rounded-xl border border-border bg-card p-3 text-center cursor-pointer" onClick={navegarAfastamentos}>
                                        <p className="text-[10px] text-muted-foreground mb-0.5">Dias trabalhados</p>
                                        <p className="text-xl font-extrabold font-display text-foreground">{d.diasTrabalhados}<span className="text-xs font-normal text-muted-foreground">/{d.totalDiasPeriodo}</span></p>
                                    </div>
                                    <div className="rounded-xl border border-border bg-card p-3 text-center cursor-pointer" onClick={navegarRotas}>
                                        <p className="text-[10px] text-muted-foreground mb-0.5">{isLogistico ? "Escalas de rota" : "Romaneios"}</p>
                                        <p className="text-xl font-extrabold font-display text-foreground">{d.quantidadesEscalasDeRota ?? "—"}</p>
                                    </div>
                                </div>
                                :
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="rounded-xl border border-border bg-card p-3 flex flex-col items-center gap-1">
                                        <CircularProgress value={Number(d.percentualConclusao)} label="Conclusão de rota" />
                                    </div>
                                    <div className="rounded-xl border border-border bg-card p-3 flex flex-col items-center gap-1 cursor-pointer" onClick={navegarTelemetria}>
                                        <CircularProgress
                                            value={Number(d.mediaSegurancaViaria)}
                                            label="Segurança viária"
                                            color={d.atingiuMeta ? "#14b8a6" : "#f43f5e"}
                                        />
                                    </div>
                                    <div className="rounded-xl border border-border bg-card p-3 text-center cursor-pointer" onClick={navegarAfastamentos}>
                                        <p className="text-[10px] text-muted-foreground mb-0.5">Dias trabalhados</p>
                                        <p className="text-xl font-extrabold font-display text-foreground">{d.diasTrabalhados}<span className="text-xs font-normal text-muted-foreground">/{d.totalDiasPeriodo}</span></p>
                                    </div>
                                    <div className="rounded-xl border border-border bg-card p-3 text-center cursor-pointer" onClick={navegarRotas}>
                                        <p className="text-[10px] text-muted-foreground mb-0.5">{isLogistico ? "Cargas" : "Romaneios"}</p>
                                        <p className="text-xl font-extrabold font-display text-foreground">{d.quantidadesEscalasDeRota ?? "—"}</p>
                                    </div>
                                </div>
                            }
                            {/* ── Partes do valor ── */}
                            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                                <div className="px-4 py-3 border-b border-border">
                                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Composição do valor</p>
                                </div>
                                <div className="divide-y divide-border">
                                    {[
                                        { label: "Cubagem", value: brl(d.valorTotalCubagem), icon: "fa fa-cube", color: "text-violet-500" },
                                        { label: "Conclusão de rota", value: brl(d.valorTotalConclusao), icon: "fa fa-map-marker", color: "text-teal-500" },
                                        { label: "Retorno", value: brl(d.valorTotalRetorno), icon: "fa fa-repeat", color: "text-blue-500" },
                                        { label: "Segurança viária", value: brl(d.valorTotalTelemetria), icon: "fa fa-shield", color: "text-amber-500" },
                                    ].map((item) => (
                                        <div key={item.label} className="flex items-center justify-between px-4 py-2.5">
                                            <div className="flex items-center gap-2.5">
                                                <i className={`${item.icon} ${item.color} text-sm w-4 text-center`} />
                                                <span className="text-sm text-foreground">{item.label}</span>
                                            </div>
                                            <span className="text-sm font-bold text-foreground">{item.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* ── Accordion sections ── */}

                            {/* Resumo geral */}
                            <Section title="Resumo de rotas" icon="fa fa-check-circle" accent="teal" defaultOpen>
                                {isLogistico ? (
                                    <>
                                        <Row label="Escalas de rota" value={d.quantidadesEscalasDeRota} />
                                        <Row label="Rotas Greenmile" value={d.totalRotas} />
                                        <Row label="Rotas SAP" value={d.totalRotasCubagem} />
                                        <Row label="Rotas com retorno" value={d.totalRotasComRetorno} />
                                        <Row label="Rotas 24h / 48h" value={`${d.rotas24Horas} / ${d.rotas48Horas}`} />
                                    </>
                                ) : (
                                    <>
                                        <Row label="Total romaneios" value={d.quantidadesEscalasDeRota} />
                                        <Row label="Romaneios em aberto" value={d.totalRomaneiosAbertos} />
                                        <Row label="Romaneios fechados" value={d.totalRomaneiosFechados} />
                                        <Row label="Rotas fretes" value={d.rotasFrete} />
                                    </>
                                )}
                                <Row label="Rotas realizadas com 1 auxiliar" value={d.percentual1Aux ? `${d.percentual1Aux}% (${d.rotas1Aux})` : "—"} />
                                <Row label="Rotas realizadas com 2 auxiliar." value={d.percentual2Aux ? `${d.percentual2Aux}% (${d.rotas2Aux})` : "—"} />
                            </Section>

                            {/* Conclusão de rota */}
                            <Section title="Conclusão de rota" icon="fa fa-map-marker" accent="teal">
                                <Row label="Total rotas" value={d.totalRotas} />
                                <Row label="Rotas no prazo" value={d.rotasNoPrazo} />
                                {isLogistico && <Row label="Com imagem" value={d.rotasComImagem} />}
                                <Row label={isLogistico ? "Rotas ajustadas (contadas como cumpridas)" : "Romaneios ajustados (contados como cumpridos)"} value={d.rotasAjustadas ?? 0} />
                                <Row label="Rotas ignoradas" value={d.rotasTotalIgnoradas} />
                                <Row label="% Conclusão no prazo" value={fmt(d.percentualConclusao)} bold />
                                {isLogistico && <Row label="% Adesão Greenmile" value={fmt(d.percentualGreenmile)} highlight />}
                                <div className="border-t border-border pt-2">
                                    <Row label="Valor conclusão" value={brl(d.valorTotalConclusao)} bold />
                                </div>
                            </Section>

                            {/* Cubagem */}
                            <Section title="Cubagem" icon="fa fa-cube" accent="violet">
                                <Row label="Cubos entregue com 1 auxiliar" value={num(d.cubosEntregue1Auxiliar)} />
                                <Row label="Cubos entregue com 2 auxiliar" value={num(d.cubosEntregue2Auxiliar)} />
                                <Row label="Cubos entregue com freteiro" value={num(d.cubosEntregueFrete)} />
                                <Row label="Total cubos entregue" value={num((d.cubosEntregue1Auxiliar ?? 0) + (d.cubosEntregue2Auxiliar ?? 0))} bold />
                                <div className="border-t border-border pt-2 space-y-2">
                                    <Row label="Valor por 1 auxiliar" value={brl(d.valorCom1Auxiliar)} />
                                    <Row label="Valor por 2 auxiliares" value={brl(d.valorCom2Auxiliar)} />
                                    <Row label="Valor a receber pela cubagem" value={brl(d.valorTotalCubagem)} bold />
                                </div>
                            </Section>

                            {/* Retorno */}
                            <Section title="Retorno de volumes" icon="fa fa-repeat" accent="blue">
                                <Row label="Cubos retornados com 1 auxiliar" value={num(d.totalCubosRetornadosCom1Aux)} />
                                <Row label="Cubos retornados com 1 auxiliar"value={num(d.totalCubosRetornadosCom2Aux)} />
                                <Row label="Total de cubos retornado" value={num(d.totalCubosRetornados)} bold />
                                <Row label="% Retorno (Entregue X Retornado)" value={fmt(d.percentualRetornoTotal)} />
                                <Row label="Faixa premiação" value={d.faixaPremiacao ?? "—"} />
                                {d.ultrapassouLimite && (
                                    <div className="flex items-center gap-1.5 text-xs text-destructive bg-destructive/10 rounded-lg px-2 py-1.5 mt-1">
                                        <i className="fa fa-exclamation-triangle" />
                                        Limite de retorno ultrapassado
                                    </div>
                                )}
                                <div className="border-t border-border pt-2">
                                    <Row label="Valor total retorno" value={brl(d.valorTotalRetorno)} bold />
                                </div>
                            </Section>

                            {!perfil.includes("AUXILIAR_ENTREGA") &&
                                <Section title="Segurança viária" icon="fa fa-shield" accent="amber">
                                    <div className="flex justify-between items-center">
                                        <span className="text-muted-foreground text-xs">Status</span>
                                        <Badge ok={d.atingiuMeta} />
                                    </div>
                                    <Row label="Média" value={fmt(d.mediaSegurancaViaria)} />
                                    <Row label="Meta" value={d.meta != null ? `${d.meta}%` : "—"} />
                                    <div className="border-t border-border pt-2">
                                        <Row label="Valor segurança viária" value={brl(d.valorTotalTelemetria)} bold />
                                    </div>
                                </Section>
                            }

                            {/* Dias trabalhados */}
                            <Section title="Dias trabalhados" icon="fa fa-calendar" accent="gray">
                                <Row label="Trabalhados" value={d.diasTrabalhados} />
                                <Row label="Afastamentos" value={d.diasAfastados} />
                                <Row label="Total no período" value={d.totalDiasPeriodo} />
                                <div className="pt-2">
                                    <div className="flex justify-between text-xs mb-1">
                                        <span className="text-muted-foreground">% Trabalhado</span>
                                        <span className="font-bold text-foreground">
                                            {fmt(d.porcentagemTrabalhado != null ? d.porcentagemTrabalhado * 100 : null)}
                                        </span>
                                    </div>
                                    <div className="bg-muted rounded-full h-2 overflow-hidden">
                                        <div
                                            className="h-2 rounded-full bg-teal-500"
                                            style={{ width: `${Math.min((d.porcentagemTrabalhado ?? 0) * 100, 100)}%` }}
                                        />
                                    </div>
                                </div>
                            </Section>

                            {/* Rodapé informativo */}
                            <div className="text-center py-4">
                                <p className="text-xs text-gray-400">
                                    Versão da regra: <span className="font-medium">{d.versaoRegra ?? "—"}</span>
                                </p>
                                {d.dataCalculo && (
                                    <p className="text-xs text-gray-400">
                                        Calculado em {formatLocalDateTime(d.dataCalculo)}
                                    </p>
                                )}
                            </div>

                        </motion.div>
                    )
                }
            </div>

            <Dialog
                header="Parâmetros Utilizados"
                visible={modalParametros}
                modal
                className="w-[99%] min-h-[400px]"
                blockScroll
                onHide={() => setModalParametros(false)}
            >
                <ParametrosSnapshotView
                    parametrosSnapshotJson={d?.parametrosSnapshotJson}
                />
            </Dialog>

        </div>
    );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (v) => (v != null ? `${Number(v).toFixed(2)}%` : "—");
const num = (v) => (v != null ? Number(v).toFixed(2) : "—");
const brl = (v) => (v != null ? `R$ ${formatarMoedaBrasileira(v)}` : "R$ 0,00");

// ─── Section accordion ───────────────────────────────────────────────────────
function Section({ title, icon, children, defaultOpen = false, accent = "teal" }) {
    const [open, setOpen] = useState(defaultOpen);
    const accents = {
        teal: "text-teal-600",
        blue: "text-blue-600",
        amber: "text-amber-600",
        rose: "text-rose-500",
        violet: "text-violet-600",
        gray: "text-gray-500",
    };
    return (
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <button
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between px-4 py-3.5 text-left"
            >
                <div className="flex items-center gap-2.5">
                    <i className={`${icon} text-base ${accents[accent]}`} />
                    <span className="font-semibold text-sm text-gray-800">{title}</span>
                </div>
                <i className={`fa fa-chevron-down text-gray-400 text-xs transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: "easeInOut" }}
                        className="overflow-hidden"
                    >
                        <div className="border-t border-border px-4 py-3 space-y-2.5 text-sm">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

// ─── Row item ─────────────────────────────────────────────────────────────────
function Row({ label, value, bold, highlight }) {
    return (
        <div className="flex justify-between items-center">
            <span className="text-gray-500 text-xs leading-snug pr-4">{label}</span>
            <span className={`text-xs text-right flex-shrink-0 ${bold ? "font-bold text-gray-900" : highlight ? "font-semibold text-teal-600" : "font-medium text-gray-800"}`}>
                {value ?? "—"}
            </span>
        </div>
    );
}

// ─── Circular progress ────────────────────────────────────────────────────────
function CircularProgress({ value, label, size = 80, stroke = 8, color = "#14b8a6" }) {
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const pct = Math.min(Math.max(value ?? 0, 0), 100);
    const dash = (pct / 100) * circ;
    return (
        <div className="flex flex-col items-center gap-1">
            <div className="relative" style={{ width: size, height: size }}>
                <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
                    <circle
                        cx={size / 2} cy={size / 2} r={r}
                        fill="none" stroke={color} strokeWidth={stroke}
                        strokeDasharray={`${dash} ${circ}`}
                        strokeLinecap="round"
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold" style={{ color }}>{pct.toFixed(0)}%</span>
                </div>
            </div>
            <span className="text-xs text-gray-500 text-center leading-tight">{label}</span>
        </div>
    );
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function Badge({ ok, labelOk = "Atingiu", labelNo = "Não atingiu" }) {
    return ok
        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700"><i className="fa fa-check" />{labelOk}</span>
        : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700"><i className="fa fa-times" />{labelNo}</span>;
}

// ─── Horizontal bar ──────────────────────────────────────────────────────────
function HBar({ label, value, max, color = "#14b8a6", suffix = "" }) {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
        <div className="flex items-center gap-2 text-xs">
            <span className="w-28 text-gray-500 truncate flex-shrink-0">{label}</span>
            <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
            </div>
            <span className="w-12 text-right font-semibold text-gray-800 flex-shrink-0">
                {typeof value === "number" ? value.toLocaleString("pt-BR", { maximumFractionDigits: 2 }) : value}{suffix}
            </span>
        </div>
    );
}

// ─── Empty / Loading state ───────────────────────────────────────────────────
function EmptyState({ loading }) {
    return (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-3">
            {loading
                ? <><i className="fa fa-spinner fa-spin text-3xl text-teal-500" /><p className="text-sm">Carregando sua comissão...</p></>
                : <><i className="fa fa-inbox text-4xl opacity-30" /><p className="text-sm">Nenhuma comissão encontrada para o período.</p></>
            }
        </div>
    );
}
